/**
 * StatHolidaysPage.tsx
 * Admin CRUD page for BC statutory holidays (Phase 1, Item 7).
 *
 * Route: /admin/stat-holidays (rendered inside AdminLayout)
 *
 * Features:
 *  - Year filter (defaults to current year, shows ±1 year either side)
 *  - DataTable-style list of holidays for the selected year
 *  - Add / Edit / Delete actions
 *  - Soft delete confirmation via ConfirmDialog
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Add, Edit, DeleteOutline, EventAvailable } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import {
  fetchStatHolidays,
  deleteStatHoliday,
} from '../../../services/scheduleStatHolidays';
import type { StatHoliday } from '../../../types/schedule';
import { StatHolidayEditDialog } from './StatHolidayEditDialog';
import { ConfirmDialog, CONFIRM_INITIAL } from '../../../components/shared/ConfirmDialog';
import type { ConfirmDialogState } from '../../../components/shared/ConfirmDialog';

// Build a small range of years around today for the filter dropdown
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

// Canadian provinces the lab tags holidays against (NB first — the lab's home region).
const REGION_OPTIONS = ['NB', 'BC', 'AB', 'ON', 'QC', 'NS', 'MB', 'SK', 'PE', 'NL'];
const DEFAULT_REGION = 'NB';

/** Format 'YYYY-MM-DD' to a readable label like 'Mon, Jan 1, 2026'. */
function formatDate(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-CA', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    timeZone: 'UTC',
  });
}

export const StatHolidaysPage = () => {
  const theme = useTheme();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [region, setRegion] = useState(DEFAULT_REGION);
  const [holidays, setHolidays] = useState<StatHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<StatHoliday | null | undefined>(undefined);
  // undefined = dialog closed; null = create mode; StatHoliday = edit mode
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchStatHolidays({ year, region });
      setHolidays(data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load holidays', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [year, region]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = (h: StatHoliday) => {
    setConfirmDlg({
      open: true,
      title: `Delete "${h.Name}"?`,
      message: `This will soft-delete the ${formatDate(h.HolidayDate)} holiday. Existing assignments that referenced it as a stat holiday will not be affected (capacity math re-checks on next load).`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await deleteStatHoliday(h.StatHolidayKey);
          setSnackbar({ open: true, message: 'Holiday deleted', severity: 'success' });
          await refresh();
        } catch {
          setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
        }
      },
    });
  };

  const handleSaved = async (saved: StatHoliday) => {
    setEditing(undefined);
    setSnackbar({
      open: true,
      message: `Saved "${saved.Name}"`,
      severity: 'success',
    });
    await refresh();
  };

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Statutory Holidays</Typography>
          <Typography variant="caption" color="text.secondary">
            {region} public holidays used for stat-aware capacity calculation in the Employee Schedule.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select value={region} onChange={(e) => setRegion(e.target.value)}>
              {REGION_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setEditing(null)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Add Holiday
          </Button>
        </Box>
      </Box>

      {/* Holiday list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={1}>
          {holidays.map((h) => (
            <Paper
              key={h.StatHolidayKey}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, 0.5),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <EventAvailable sx={{ color: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {h.Name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(h.HolidayDate)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Chip
                  label={h.Region}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditing(h)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(h)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}

          {holidays.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: 'divider' }}>
              <Typography color="text.secondary">
                No {region} holidays for {year}. Click "Add Holiday" to add one, or run the seed
                script to populate holidays for 2026–2028.
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* Edit / Create dialog */}
      {editing !== undefined && (
        <StatHolidayEditDialog
          holiday={editing}
          open={editing !== undefined}
          defaultRegion={region}
          onClose={() => setEditing(undefined)}
          onSaved={handleSaved}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete confirmation */}
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
